import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Drawer,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { useLocation } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { ClaimStage } from '../../../interfaces/billing';
import { BillingProcess } from '../../../interfaces/billing.rpm';
import { IClinic } from '../../../interfaces/clinic';
import { getClinicNameByClinicId } from '../../../lib/clinic';
import { BillingService } from '../../../services';
import { CommonService } from '../../../services/common';
import { formatDateString } from '../../../util/dateFormat';
import { ClaimSummary } from './ClaimSummary';
import { ProcessStatusBadge } from './ProcessStatusBadge';
import { processPollingService } from '../../../services/ProcessPollingService';
import { isActiveStatus } from './ProcessStatusIndicator';

function getTabNameByProcessStatus(status: ClaimStage) {
  const tabNameByProcessStatus = {
    [ClaimStage.CreationInProgress]: 'Patient Selection',
    [ClaimStage.Created]: 'RPM Calculation',
    [ClaimStage.ExportInProgress]: 'RPM Calculation',
    [ClaimStage.Exported]: 'RPM Export Results',
    [ClaimStage.Discarded]: 'Process Cancelled',
  };

  if (!status || !tabNameByProcessStatus[status]) {
    return 'Patient Selection';
  }

  return tabNameByProcessStatus[status];
}

export const ProcessesPanel = () => {
  const [selectedProcess, setSelectedProcess] = useState<BillingProcess | null>(null);
  const [isFetchingProcesses, setIsFetchingProcesses] = useState(false);
  const [clinics, setClinics] = useState<IClinic[] | null>(null);
  const [processes, setProcesses] = useState<BillingProcess[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // Filter states
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<ClaimStage | 'all'>('all');
  const [clinicFilter, setClinicFilter] = useState<number | 'all'>('all');
  const [userFilter, setUserFilter] = useState('');

  const notify = useNotify();
  const location = useLocation();

  // Handle real-time process updates
  const handleProcessUpdates = useCallback((updatedProcesses: BillingProcess[]) => {
    setProcesses(updatedProcesses);
    setLastRefreshed(new Date());
    
    // Update selected process if it exists in the updated list
    // Only update if the process data actually changed to avoid UI flickering
    if (selectedProcess) {
      const updatedSelectedProcess = updatedProcesses.find(p => p.id === selectedProcess.id);
      if (updatedSelectedProcess && 
          (updatedSelectedProcess.status !== selectedProcess.status || 
           updatedSelectedProcess.lastModifiedAt !== selectedProcess.lastModifiedAt)) {
        setSelectedProcess(updatedSelectedProcess);
      }
    }
  }, [selectedProcess]);

  const fetchClinics = useCallback(async () => {
    try {
      const clinics = await CommonService.getClinics();

      setClinics(clinics);
    } catch (error) {
      notify('Could not load clinics', 'error');
    }
  }, [notify]);

  const fetchProcesses = useCallback(async () => {
    setIsFetchingProcesses(true);
    const billingServiceClient = new BillingService();
    const allProcesses = await billingServiceClient.getProcesses();
    const croppedList = allProcesses?.slice(0, 100); // TODO: remove this when the endpoint is paginated
    if (croppedList?.length) {
      setProcesses(croppedList);
    }
    setIsFetchingProcesses(false);
    setLastRefreshed(new Date());
    return croppedList;
  }, []);

  // Start RPM process polling for all processes
  useEffect(() => {
    processPollingService.startRpmProcessPolling({}, handleProcessUpdates);
    
    return () => {
      processPollingService.stopRpmProcessPolling();
    };
  }, [handleProcessUpdates]);

  const handleAfterAction = useCallback(async (currentProcessId: number | null) => {
    // Only refetch if we really need to (when process is created/action is taken)
    // Global polling will handle status updates automatically
    const newProcesses = await fetchProcesses();
    
    if (currentProcessId) {
      const newSelectedProcess = newProcesses?.find(({ id }) => id === currentProcessId);
      if (newSelectedProcess) {
        setSelectedProcess(newSelectedProcess);
      }
    } else {
      setSelectedProcess(null);
    }
  }, [fetchProcesses]);

  const handleNewProcess = useCallback(() => {
    setSelectedProcess(null);
    setIsDrawerOpen(false);
  }, []);

  const handleProcessSelection = useCallback((process: BillingProcess) => {
    setSelectedProcess(process);
    setIsDrawerOpen(false);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  // Filter functions
  const clearFilters = useCallback(() => {
    setSearchText('');
    setStatusFilter('all');
    setClinicFilter('all');
    setUserFilter('');
  }, []);

  const filteredProcesses = useCallback(() => {
    return processes.filter(process => {
      // Search text filter (ID, status, user names)
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const matchesId = process.id.toString().includes(searchLower);
        const matchesStatus = getTabNameByProcessStatus(process.status).toLowerCase().includes(searchLower);
        const matchesStartedBy = process.startedByUserFullName?.toLowerCase().includes(searchLower);
        const matchesModifiedBy = process.lastModifiedByUserFullName?.toLowerCase().includes(searchLower);
        
        if (!matchesId && !matchesStatus && !matchesStartedBy && !matchesModifiedBy) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== 'all' && process.status !== statusFilter) {
        return false;
      }

      // Clinic filter
      if (clinicFilter !== 'all') {
        const processClinicIds = process.creationParameters?.filters?.clinicIds || [];
        if (!processClinicIds.includes(clinicFilter as number)) {
          return false;
        }
      }

      // User filter
      if (userFilter) {
        const userLower = userFilter.toLowerCase();
        const matchesStartedBy = process.startedByUserFullName?.toLowerCase().includes(userLower);
        const matchesModifiedBy = process.lastModifiedByUserFullName?.toLowerCase().includes(userLower);
        
        if (!matchesStartedBy && !matchesModifiedBy) {
          return false;
        }
      }

      return true;
    });
  }, [processes, searchText, statusFilter, clinicFilter, userFilter]);

  // Get unique users for filter dropdown
  const uniqueUsers = useCallback(() => {
    const users = new Set<string>();
    processes.forEach(process => {
      if (process.startedByUserFullName) users.add(process.startedByUserFullName);
      if (process.lastModifiedByUserFullName) users.add(process.lastModifiedByUserFullName);
    });
    return Array.from(users).sort();
  }, [processes]);

  // Get unique clinics that have processes
  const usedClinics = useCallback(() => {
    const clinicIds = new Set<number>();
    processes.forEach(process => {
      process.creationParameters?.filters?.clinicIds?.forEach(id => clinicIds.add(id));
    });
    return Array.from(clinicIds).map(id => clinics?.find(c => c.id === id)).filter(Boolean) as IClinic[];
  }, [processes, clinics]);

  useEffect(() => {
    fetchProcesses();
    fetchClinics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select process based on URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const processIdParam = urlParams.get('processId');
    
    if (processIdParam && processes.length > 0) {
      const processId = parseInt(processIdParam, 10);
      const targetProcess = processes.find(process => process.id === processId);
      
      if (targetProcess) {
        setSelectedProcess(targetProcess);
      }
    }
  }, [location.search, processes]);

  return (
    <Stack gap={2} display='flex' width='100%'>
      {/* Header with buttons - aligned with table content */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center"
        sx={{ px: 2 }}
      >
        <Typography variant='h4'>RPM Core Process</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewProcess}
            color="primary"
          >
            New Process
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => setIsDrawerOpen(true)}
            color="primary"
          >
            Process History
          </Button>
        </Box>
      </Box>

      {/* Main content area - ClaimSummary takes full width */}
      <Box width="100%">
        <ClaimSummary
          process={selectedProcess}
          clinics={clinics ?? []}
          onAfterAction={handleAfterAction}
        />
      </Box>

      {/* Process History Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            padding: 2,
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant='h6'>Process History</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {lastRefreshed && (
              <Typography variant="caption" color="text.secondary">
                Last updated: {lastRefreshed.toLocaleTimeString()}
              </Typography>
            )}
            <Tooltip title="Refresh process list">
              <IconButton
                size="small"
                onClick={fetchProcesses}
                disabled={isFetchingProcesses}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filter Controls */}
        <Box mb={2}>
          <Stack spacing={2}>
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search by ID, status, or user..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            
            {/* Status Filter */}
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ClaimStage | 'all')}
                input={<OutlinedInput label="Status" />}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value={ClaimStage.CreationInProgress}>Creation In Progress</MenuItem>
                <MenuItem value={ClaimStage.Created}>Created</MenuItem>
                <MenuItem value={ClaimStage.ExportInProgress}>Export In Progress</MenuItem>
                <MenuItem value={ClaimStage.Exported}>Exported</MenuItem>
                <MenuItem value={ClaimStage.Discarded}>Cancelled</MenuItem>
              </Select>
            </FormControl>

            {/* Clinic Filter */}
            <FormControl size="small">
              <InputLabel>Clinic</InputLabel>
              <Select
                value={clinicFilter}
                onChange={(e) => setClinicFilter(e.target.value as number | 'all')}
                input={<OutlinedInput label="Clinic" />}
              >
                <MenuItem value="all">All Clinics</MenuItem>
                {usedClinics().map((clinic) => (
                  <MenuItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* User Filter */}
            <FormControl size="small">
              <InputLabel>User</InputLabel>
              <Select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                input={<OutlinedInput label="User" />}
              >
                <MenuItem value="">All Users</MenuItem>
                {uniqueUsers().map((user) => (
                  <MenuItem key={user} value={user}>
                    {user}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clear Filters Button */}
            {(searchText || statusFilter !== 'all' || clinicFilter !== 'all' || userFilter) && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                variant="outlined"
                color="secondary"
              >
                Clear Filters
              </Button>
            )}
          </Stack>
        </Box>

        <List component='nav'>
          {isFetchingProcesses ? (
            <Stack alignItems='center' gap={2} margin={5}>
              <CircularProgress />
              <Typography variant='body2'>Fetching processes</Typography>
            </Stack>
          ) : (
            <>
              {filteredProcesses().length === 0 ? (
                <Stack alignItems='center' gap={2} margin={5}>
                  <Typography variant='body2' color='text.secondary'>
                    No processes found matching the current filters
                  </Typography>
                </Stack>
              ) : (
                filteredProcesses().map((process) => (
                <ListItemButton
                  key={process.id}
                  selected={selectedProcess?.id === process.id}
                  onClick={() => handleProcessSelection(process)}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: isActiveStatus(process.status) 
                      ? 'rgba(25, 118, 210, 0.04)' 
                      : 'inherit',
                    '&:hover': {
                      backgroundColor: isActiveStatus(process.status) 
                        ? 'rgba(25, 118, 210, 0.08)' 
                        : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <Stack spacing={1} width="100%">
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight="medium">
                              #{process.id} {getTabNameByProcessStatus(process.status)}
                            </Typography>
                            <ProcessStatusBadge status={process.status} />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {isActiveStatus(process.status) 
                              ? `Started by ${process.startedByUserFullName} at ${formatDateString(process.createdAt)}`
                              : `Last updated by ${process.lastModifiedByUserFullName} at ${formatDateString(process.lastModifiedAt)}`
                            }
                          </Typography>
                        }
                      />
                    </Box>
                    {clinics?.length &&
                      !!process.creationParameters?.filters?.clinicIds?.length && (
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {process.creationParameters.filters.clinicIds.map((clinicId) => (
                            <Chip 
                              key={clinicId}
                              label={getClinicNameByClinicId(clinicId, clinics)} 
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                  </Stack>
                </ListItemButton>
                ))
              )}
            </>
          )}
        </List>
      </Drawer>
    </Stack>
  );
};
